import * as Mui from "@mui/material";
import * as MuiIcon from "@mui/icons-material";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import dayjs from "dayjs";
import { useContext, useState } from "react";
import { useForm } from "react-hook-form";
import SectionHeader from "../../../../util/component/SectionHeader";
import { ContextOperation } from "../../context/OperationContext";
import { useAgGridCustomDatasource } from "../../hooks/useAgGridCustomDatasource";
import useOperationTranslation from "../../hooks/useOperationTranslation";
import useSnackbar from "../../hooks/useSnackbar";
import { saveFileFromResponse } from "../../utils";
import AgGridOperationTable from "../common/AgGridOperationTable";
import { FileTextWithIcon } from "../common/FileTextWithIcon";
import { nameof } from "../common/ObjectUtils";
import ETollImportFilterAndSearch from "./ETollImportFilterAndSearch";
import ETollImportService from "./service/ETollImportService";
import { ETollTypes } from "./tasks/Types";

const ETollImportTracking = () => {
  const { tenant, setLoading } = useContext(ContextOperation);
  const t = useOperationTranslation();

  const snackbar = useSnackbar();

  const form = useForm<ETollTypes.IFormInput>();

  const [refreshCount, setRefreshCount] = useState(0);

  const columnDefs: ColDef<ETollTypes.IETollResponse>[] = [
    {
      field: nameof<ETollTypes.IETollResponse>("filename"),
      headerName: t("eTollImport.eTollFile"),
      cellRenderer: (params: ICellRendererParams<ETollTypes.IETollResponse>) => {
        return (
          <FileTextWithIcon
            onDownloadFile={() => {
              exportFileImportTrackingDataById(params.data?.id);
            }}
            fileTextDownload={params.value}
          />
        );
      },
    },
    {
      field: nameof<ETollTypes.IETollResponse>("startDatetime"),
      headerName: t("eTollImport.startDate"),
      cellRenderer: (params: ICellRendererParams<ETollTypes.IETollResponse>) => {
        return (
          <>
            {params.value ? (
              <Mui.Box component="span" width="inherit" height="inherit">
                {dayjs(params.value).format("DD/MM/YYYY-HH:mm")}
              </Mui.Box>
            ) : (<Mui.Box> </Mui.Box>)}
          </>
        );
      },
    },
    {
      field: nameof<ETollTypes.IETollResponse>("endDatetime"),
      headerName: t("eTollImport.endDate"),
      cellRenderer: (params: ICellRendererParams<ETollTypes.IETollResponse>) => {
        return (
          <>
            {params.value ? (
              <Mui.Box component="span" width="inherit" height="inherit">
                {dayjs(params.value).format("DD/MM/YYYY-HH:mm")}
              </Mui.Box>
            ) : (<Mui.Box> </Mui.Box>)}
          </>
        );
      },
    },
    {
      field: nameof<ETollTypes.IETollResponse>("errorFilename"),
      headerName: t("eTollImport.failReport"),
      cellRenderer: (params: ICellRendererParams<ETollTypes.IETollResponse>) => {
        if (!!params.value) {
          return (
            <FileTextWithIcon
              onDownloadFile={() => {
                exportErrorFileImportTrackingDataById(params.data?.id);
              }}
              fileTextDownload={params.value}
              fileTextColorRed={true}
            />
          );
        } else { return params.value; }
      },
    },
    {
      field: nameof<ETollTypes.IETollResponse>("status"),
      headerName: t("eTollImport.status"),
      cellRenderer: (params: ICellRendererParams<ETollTypes.IETollResponse>) => StatusColumn(params.data),
    },
  ];

  const StatusColumn = (params: ETollTypes.IETollResponse | undefined) => {
    if (!params) {
      return;
    }

    switch (params.status) {
      case "PROCESSED":
        return (
          <Mui.Chip
            label={t("eTollImport.statusType.processed")}
            variant="filled"
            color="success"
          />
        );
      case "PENDING":
        return (
          <Mui.Chip
            label={t("eTollImport.statusType.pending")}
            variant="filled"
            color="warning"
          />
        );
      case "WITH_ERRORS_FORMAT":
        return (
          <Mui.Chip
            label={t("eTollImport.statusType.errorFormat")}
            variant="filled"
            color="error"
          />
        );
      case "WITH_ERRORS_DATA_INTEGRITY":
        return (
          <Mui.Chip
            label={t("eTollImport.statusType.dataIntegrityError")}
            variant="filled"
            color="error"
          />
        );
      case "WITH_ERRORS_GENERIC_ERROR":
        return (
          <Mui.Chip
            label={t("eTollImport.statusType.genericError")}
            variant="filled"
            color="error"
          />
        );
      default: return "";
    }
  };

  const getExcel = () => {
    setLoading(true);

    ETollImportService.get.excel(tenant, form.getValues()).then(
      (response: any) => {
        setLoading(false);
        saveFileFromResponse(response);
      },
      error => {
        setLoading(false);
        snackbar.show("error", t("eTollImport.errorDownloadFile"));
      }
    )
  }

  const exportFileImportTrackingDataById = async (id: number | undefined) => {
    if (!id) {
      snackbar.show("warning", "No id found for this file");
      return;
    }

    setLoading(true);
    ETollImportService.get.file(tenant, id).then(
      (response: any) => {
        setLoading(false);
        saveFileFromResponse(response);
      },
      error => {
        setLoading(false);
        snackbar.show("error", t("eTollImport.errorDownloadFile"));
      }
    );
  };

  const exportErrorFileImportTrackingDataById = (id: number | undefined) => {
    if (!id) {
      snackbar.show("warning", "No id found for this file");
      return;
    }

    setLoading(true);
    ETollImportService.get.errorFile(tenant, id).then(
      (response: any) => {
        setLoading(false);
        saveFileFromResponse(response);
      },
      error => {
        setLoading(false);
        snackbar.show("error", t("eTollImport.errorDownloadErrorFile"));
      }
    );
  };

  const [datasource, length] = useAgGridCustomDatasource(
    ETollImportService.get.all,
    [tenant, form.getValues()],
    [form.formState.submitCount, refreshCount]
  );

  return (
    <>
      <SectionHeader
        title={t("eTollImport.titleFirst") + " " + t("eTollImport.titleSecond")}
      />

      <ETollImportFilterAndSearch form={form} />

      <Mui.Box m={2}>
        <Mui.Box>
          <Mui.Card>
            <Mui.Box
              p={1}
              display="flex"
              justifyContent={"flex-end"}
              alignItems="center"
              gap={2}
            >
              <Mui.Tooltip title={t("tooltipTitle.refresh")}>
                <Mui.Button
                  variant="text"
                  size="icon"
                  onClick={() => setRefreshCount(refreshCount + 1)}
                >
                  <MuiIcon.Cached />
                </Mui.Button>
              </Mui.Tooltip>

              <Mui.Tooltip title={t("tooltipTitle.download")}>
                <Mui.Button
                  variant="text"
                  size="icon"
                  color="accent"
                  disabled={length === 0 ? true : false}
                  onClick={getExcel}
                >
                  <MuiIcon.FileDownload />
                </Mui.Button>
              </Mui.Tooltip>
            </Mui.Box>
          </Mui.Card>
        </Mui.Box>

        <Mui.Box>
          <AgGridOperationTable
            columnDefs={columnDefs}
            serverSideDatasource={datasource}
            totalElements={length}
          />
        </Mui.Box>
      </Mui.Box>
    </>
  )
}
export default ETollImportTracking;
